import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, ArrowLeft, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';

const ResetPasswordPage = () => {
  const { token: pathToken } = useParams();
  const [searchParams] = useSearchParams();
  const queryToken = searchParams.get('token');
  // Use query param first, then path param as fallback
  const token = queryToken || pathToken;
  
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Token bulunamadı');
        setIsVerifying(false);
        return;
      }
      
      try {
        const response = await fetch(`${backendUrl}/api/auth/verify-reset-token/${token}`);
        const data = await response.json();

        if (response.ok && data.valid) {
          setIsValid(true);
          setEmail(data.email);
        } else {
          setError(data.detail || 'Geçersiz veya süresi dolmuş bağlantı');
        }
      } catch (err) {
        setError('Bağlantı doğrulanamadı');
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token, backendUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Şifreler eşleşmiyor');
      return;
    }

    if (password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${backendUrl}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Bir hata oluştu');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-4 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Bağlantı doğrulanıyor...</p>
        </div>
      </div>
    );
  }

  if (!isValid && !success) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-4">
        <div className="max-w-md mx-auto">
          <div className="bg-white p-8 shadow-sm border text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-light tracking-wider mb-2">Geçersiz Bağlantı</h1>
            <p className="text-gray-600 text-sm mb-6">{error}</p>
            <Link
              to="/sifremi-unuttum"
              className="inline-block bg-black text-white px-6 py-3 text-sm tracking-wider hover:bg-gray-900 transition-colors"
            >
              Yeni Bağlantı İste
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-4">
        <div className="max-w-md mx-auto">
          <div className="bg-white p-8 shadow-sm border text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-light tracking-wider mb-2">Şifre Güncellendi</h1>
            <p className="text-gray-600 text-sm mb-6">
              Şifreniz başarıyla güncellendi. Yeni şifrenizle giriş yapabilirsiniz.
            </p>
            <Link
              to="/giris"
              className="inline-block bg-black text-white px-6 py-3 text-sm tracking-wider hover:bg-gray-900 transition-colors"
            >
              Giriş Yap
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20 px-4">
      <div className="max-w-md mx-auto">
        <div className="bg-white p-8 shadow-sm border">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-light tracking-wider mb-2">YENİ ŞİFRE BELİRLE</h1>
            <p className="text-gray-600 text-sm">
              <span className="font-medium">{email}</span> için yeni şifre belirleyin.
            </p>
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-red-50 text-red-600 p-3 mb-4 text-sm">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Yeni Şifre *</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="En az 6 karakter"
                  className="w-full pl-10 pr-12 py-3 border border-gray-200 focus:outline-none focus:border-black"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Şifre Tekrar *</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Şifrenizi tekrar girin"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 focus:outline-none focus:border-black"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-black text-white py-3 text-sm tracking-wider hover:bg-gray-900 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'GÜNCELLENİYOR...' : 'ŞİFREYİ GÜNCELLE'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/giris" className="text-sm text-gray-600 hover:text-black flex items-center justify-center gap-2">
              <ArrowLeft size={14} />
              Giriş sayfasına dön
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
